import { AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useDeactivationImpact } from "@/hooks/use-categories";

interface DeactivateEntityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entityType: "category" | "subtask";
  entityId: string;
  entityName: string;
  isPending: boolean;
  onConfirm: () => void;
}

export function DeactivateEntityDialog({
  open,
  onOpenChange,
  entityType,
  entityId,
  entityName,
  isPending,
  onConfirm,
}: DeactivateEntityDialogProps) {
  const { data: impact, isLoading } = useDeactivationImpact(entityType, entityId, open);

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Deactivate {entityType}?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div>
              <p>
                Deactivating <strong className="text-foreground">{entityName}</strong> will hide it
                from engineers going forward. Existing submitted entries are not affected.
              </p>

              {isLoading && <p className="mt-3 text-sm text-muted-foreground">Checking impact…</p>}

              {impact && (impact.unsubmittedEntries > 0 || impact.activeWOsLast30Days > 0) && (
                <div className="callout callout--warn mt-3">
                  <AlertTriangle className="callout__icon w-4 h-4" />
                  <div className="text-sm">
                    <strong className="callout__title">Active usage detected</strong>
                    {impact.unsubmittedEntries > 0 && (
                      <p>
                        {impact.unsubmittedEntries} unsubmitted time{" "}
                        {impact.unsubmittedEntries === 1 ? "entry" : "entries"} currently reference
                        this.
                      </p>
                    )}
                    {impact.activeWOsLast30Days > 0 && (
                      <p>
                        Used on {impact.activeWOsLast30Days} active work{" "}
                        {impact.activeWOsLast30Days === 1 ? "order" : "orders"} in the last 30 days.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={isPending || isLoading}>
            {isPending ? "Deactivating..." : "Deactivate"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
